import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { Session } from "@/types/auth"
import type { ApiResource } from "@/lib/api/types"
import type {
  ApiBrand,
  ApiOrganisation,
  ApiTenantOrganisation,
  Brand,
  OnboardingSettingsInput,
  Organisation,
  OrganisationImage,
  OrganisationSettingsInput,
  TenantOrganisation,
} from "@/types/organisation"

export function toOrganisation(data: ApiOrganisation): Organisation {
  return {
    id: data.id,
    name: data.name,
    url: data.url,
    logoUrl: data.logo_url,
    iconUrl: data.icon_url,
    primaryColour: data.primary_colour,
    fcaReference: data.fca_reference,
    companyNumber: data.company_number,
    status: data.status,
    memberCount: data.member_count,
    createdAt: data.created_at,
  }
}

function toTenantOrganisation(data: ApiTenantOrganisation): TenantOrganisation {
  return {
    ...toOrganisation(data),
    dbsRecheckMonths: data.dbs_recheck_months,
    sendDueDays: data.send_due_days,
    reviewOpensAutomatically: data.review_opens_automatically,
    disclosuresNeedClosing: data.disclosures_need_closing,
  }
}

export const organisationQueries = {
  brand: (organisation: string) =>
    queryOptions({
      queryKey: apiKey("/brand", { organisation }),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<{ data: ApiBrand }>("/brand", {
          anonymous: true,
          params: { organisation },
          signal,
        })

        return {
          id: data.id,
          name: data.name,
          logoUrl: data.logo_url,
          iconUrl: data.icon_url,
          primaryColour: data.primary_colour,
        } satisfies Brand
      },
      retry: false,
      staleTime: 5 * 60 * 1000,
    }),

  current: () =>
    queryOptions({
      queryKey: apiKey("/tenant"),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiTenantOrganisation>>(
          "/tenant",
          { signal }
        )
        return toTenantOrganisation(data)
      },
    }),
}

function storeOrganisation(
  queryClient: QueryClient,
  organisation: TenantOrganisation
) {
  queryClient.setQueryData(apiKey("/tenant"), organisation)
  queryClient.setQueryData<Session>(apiKey("/auth/session"), (session) =>
    session
      ? {
          ...session,
          organisations: session.organisations.map((membership) =>
            membership.id === organisation.id
              ? {
                  ...membership,
                  name: organisation.name,
                  url: organisation.url,
                  logoUrl: organisation.logoUrl,
                  iconUrl: organisation.iconUrl,
                }
              : membership
          ),
        }
      : session
  )
}

export function useUpdateOrganisationSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: OrganisationSettingsInput) => {
      const { data } = await apiFetch<ApiResource<ApiTenantOrganisation>>(
        "/settings/organisation",
        {
          method: "PATCH",
          body: {
            name: input.name,
            primary_colour: input.primaryColour,
          },
        }
      )
      return toTenantOrganisation(data)
    },
    onSuccess: (organisation) => {
      storeOrganisation(queryClient, organisation)
    },
  })
}

export function useUpdateOnboardingSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: OnboardingSettingsInput) => {
      const { data } = await apiFetch<ApiResource<ApiTenantOrganisation>>(
        "/settings/organisation",
        { method: "PATCH", body: input }
      )
      return toTenantOrganisation(data)
    },
    onSuccess: (organisation) => {
      storeOrganisation(queryClient, organisation)
    },
  })
}

export function useUploadOrganisationImage(image: OrganisationImage) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.set("image", file)

      const { data } = await apiFetch<ApiResource<ApiTenantOrganisation>>(
        `/settings/organisation/${image}`,
        { method: "POST", body }
      )
      return toTenantOrganisation(data)
    },
    onSuccess: (organisation) => {
      storeOrganisation(queryClient, organisation)
    },
  })
}

export function useRemoveOrganisationImage(image: OrganisationImage) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiFetch<ApiResource<ApiTenantOrganisation>>(
        `/settings/organisation/${image}`,
        { method: "DELETE" }
      )
      return toTenantOrganisation(data)
    },
    onSuccess: (organisation) => {
      storeOrganisation(queryClient, organisation)
    },
  })
}
